import React, { useContext, useEffect } from 'react';
import { equals } from 'ramda';
import {
  StyledEngineProvider,
  Theme,
  ThemeProvider,
} from '@mui/material/styles';
import { withStyles } from 'tss-react/mui';
import classNames from 'classnames';
import EDPaper from '../../../../../../../BaseComponents/Paper/EDPaper';
import {
  IAnalys,
  IColumn,
  ISumCategory,
} from '../../Queries/EkonomiskAnalysSettingsQuery/EkonomiskAnalysSettingsQuery';
import {
  ISelectedAnalys,
  ISelectedAnalysAnalys,
  ISelectedAnalysCodePrivilege,
} from '../../EkonomiskAnalysPage';
import { IUIUrval } from '../../../../../../../Types/UrvalTypes';
import EkonomiskAnalysSideMenu from '../SideMenu/EkonomiskAnalysSideMenu';
import EkonomiskAnalysChartModal from '../EkonomiskAnalysChartModal/EkonomiskAnalysChartModal';
import { IParamValue } from '../../Queries/EkonomiskAnalysChartQuery/EkonomiskAnalysChartQuery';
import EkonomiskAnalysNoAnalysSelectedPage from '../ErrorPages/EkonomiskAnalysNoAnalysSelectedPage/EkonomiskAnalysNoAnalysSelectedPage';
import EkonomiskAnalysExcelModal from '../EkonomiskAnalysExcelModal/EkonomiskAnalysExcelModal';
import { AvenyCollectionMap } from '../../../../../../../Model/ModelContext/ModelContext';
import {
  EkonomiskAnalysContextProvider,
  IAnalysResultColumn,
} from '../Context/EkonomiskAnalysContext';
import {
  IAnalysSettingsContext,
  IEkonomiskAnalysDates,
  IEkonomiskAnalysFilledDates,
} from '../AnalysSettingsHandler/AnalysSettingsHandler';
import { IUserInfo } from '../../Utils/ekonomiskAnalysInitQueryUtils';
import { StandardColumnType } from '../SideMenu/Components/PeriodOchKolumner/EditColumn/Components/Queries/EkonomiskAnalysStandardColumnQuery/EkonomiskAnalysStandardColumnQuery';
import EkonomiskAnalysYearValidator from '../InvalidYearDialog/EkonomiskAnalysYearValidator';
import EkonomiskAnalysPeriodComponent from '../EkonomiskAnalysPeriodComponent/EkonomiskAnalysPeriodComponent';
import {
  dateHasError,
  DateInputType,
} from '../../../../../../../Utils/dateUtils';
import EkonomiskAnalysHeader from '../EkonomiskAnalysHeader/EkonomiskAnalysHeader';
import EkonomiskAnalysPrintPDFModal from '../EkonomiskAnalysPrintPDFModal/EkonomiskAnalysPrintPDFModal';
import ExportToPDF from '../../../../../../../Components/ExportToPDF/ExportToPDF';
import { IEkonomiskAnalysPDF } from '../../../../../../../Components/ExportToPDF/ExportToPDFArgumentTypes';
import EkonomiskAnalysTableBase from '../EkonomiskAnalysTable/EkonomiskAnalysTableBase';
import EnhancedBokforingsarActions, {
  IsPeriodLockedFnType,
} from '../../../../../../../Components/BokfaringsarActions/BokforingsarActions';
import UpdateOverlay from '../../../../../../../Components/UpdateOverlay/UpdateOverlay';
import { kodplaner_ekkodplaner } from '../../../../../../../Components/KodplanManager/Queries/_QueryTypes/kodplaner';
import { USE_NEW_AS_START_ANALYS_ID } from '../SideMenu/EkonomiskAnalysSelect/Components/FavoriteAnalysDialog/EkonomiskAnalysFavoriteAnalysDialog';
import { NEW_ANALYS_RAPPORT_ID } from '../SideMenu/Components/CreateNewAnalys/CreateNewAnalysUtils';
import EkonomiskAnalysHasPeriodiseradBudgetQuery from '../SideMenu/Components/Other/EkonomiskAnalysHasPeriodiseradBudgetQuery/EkonomiskAnalysHasPeriodiseradBudgetQuery';
import { getForceHelarsJamforelse } from '../../Utils/userInfoUtils';
import { findCollectionStatic } from '../../../../../../../Model/avenyStaticModelUtils';
import { EmptyReason } from '../../../../../_Common/Components/BusinessCollectionComponents/EmptyReason/EmptyReason';
import EkonomiskAnalysColumnSelectDialog from '../../Components/SideMenu/Components/PeriodOchKolumner/SelectColumn/EkonomiskAnalysColumnSelectDialog';
import AvailableColumnTemplatesHandler from '../SideMenu/Components/PeriodOchKolumner/Queries/AvailableColumnTemplatesHandler';
import { AnalysInitQuery_logo } from '../../Queries/EkonomiskAnalysInitQuery/_QueryTypes/AnalysInitQuery';
import EkonomiskAnalysSaveDBSettingMutation, {
  EkonomiskAnalysSaveDBSettingType,
} from '../../Queries/EkonomiskAnalysSaveDBSettingMutation/EkonomiskAnalysSaveDBSettingMutation';
import {
  BudgetSummationModeType,
  SummationModeType,
} from '../../Utils/SummationModeUtils';
import EDTabs from '../../../../../../../BaseComponents/Tabs/EDTabs';
import EDTab from '../../../../../../../BaseComponents/Tabs/Tab/EDTab';
import { EkonomiskAnalysSammanfattning } from '../EkonomiskAnalysSammanfattning/EkonomiskAnalysSammanfattning';
import EDBox from '../../../../../../../BaseComponents/EDBox/EDBox';
import {
  ANALYSKATEGORI_ID_VISA_SAMMANFATTNING,
  AnalysLevelEnum,
  AnalysTypeEnum,
  SpecifikationTypeEnum,
} from '../../Const/EkonomiskAnalysConsts';
import EDButton from '../../../../../../../BaseComponents/Buttons/Button/EDButton';
import FunctionItem from '../../../../../_Common/Components/FunctionMenu/FunctionItem/FunctionItem';
import FunctionMenu from '../../../../../_Common/Components/FunctionMenu/FunctionMenu';
import DetaljanalysPrintModal, {
  IPrintSections,
} from '../EkonomiskAnalysSammanfattning/DetaljanalysPrintModal';
import EDIconButton from '../../../../../../../BaseComponents/Buttons/IconButton/EDIconButton';
import { AvenyThemeDark } from '../../../../../../../BaseComponents/ThemeProvider/AvenyTheme';
import { createPortal } from 'react-dom';
import {
  BreadcrumbsContext,
  HEADER,
} from '../../../../../_Common/Components/RutinBreadcrumbs/BreadcrumbsContext';
import RouterLink from '../../../../../../../Components/RouterLink/RouterLink';
import {
  buildBasePath,
  useBaseRouteValue,
} from '../../../../../../Routing/Utils/routerUtils';

interface IProps {
  analysType: AnalysTypeEnum;
  analysLevel: AnalysLevelEnum;
  activeDatabaseName: string;
  activeKodplan: kodplaner_ekkodplaner;
  analysIdFromUrl?: string | null;
  availableCategories: ISumCategory[];
  codeSystemId: string;
  collectionMap: AvenyCollectionMap;
  drilledFromDetaljanalys: boolean;
  hasRutinStartError: boolean;
  hasWholeCodeBook: boolean;
  impersonatedCodePrivilege?: ISelectedAnalysCodePrivilege;
  impersonatedCodePrivilegeChanged: boolean;
  logo: AnalysInitQuery_logo;
  onSelectedAnalysUpdated: (
    analys: ISelectedAnalysAnalys,
    codePrivilege: ISelectedAnalysCodePrivilege,
    unsavedAnalys?: boolean,
    callback?: () => void
  ) => void;
  originalStartDates: IEkonomiskAnalysDates;
  selectedAnalys: ISelectedAnalys | null;
  setStartDates: (newStartDates: IEkonomiskAnalysDates) => void;
  settingsContext: IAnalysSettingsContext;
  sideMenuOpen: boolean;
  standardColumns: StandardColumnType[] | undefined;
  toggleSideMenuOpen: (value: boolean, callback?: () => void) => void;
  updateImpersonatedCodePrivilege: (
    codePrivilege?: ISelectedAnalysCodePrivilege
  ) => void;
  unsavedAnalys: boolean;
  userInfo?: IUserInfo;
  classes?: any;
}

interface IExtendedProps extends IProps {
  isPeriodLocked: IsPeriodLockedFnType;
  availableCategories: ISumCategory[];
  saveDBSetting: EkonomiskAnalysSaveDBSettingType;
}

interface IBaseLocalAnalys extends IAnalys {
  urval: IUIUrval | null;
  detaljUrval?: IUIUrval;
  other: {
    budgetSummationMode: BudgetSummationModeType;
    includeEjBokfordaJournaler: boolean;
    denomination: number;
    decimals: number;
    showComparison: boolean;
    summationMode: SummationModeType;
    exkluderaPersonalKostnader: boolean;
    expandedSumIndex: number;
  };
}

export interface IFilledLocalAnalys extends IBaseLocalAnalys {
  dates: IEkonomiskAnalysFilledDates;
}

export interface ILocalAnalys extends IBaseLocalAnalys {
  dates: IEkonomiskAnalysDates;
}

export enum SidePanels {
  select,
  urval,
  columns,
  other,
}

export interface IEkonomiskAnalysCardSettings {
  kodbehorighetId: string;
  analysId: string;
  dates: IEkonomiskAnalysFilledDates;
  params: IParamValue[];
}

interface IState {
  urvalOchSummeringExpanded: boolean;
  columnsExpanded: boolean;
  otherExpanded: boolean;
  showChartModal: boolean;
  showExcelModal: boolean;
  showDetaljanalysPrintModal: boolean;
  showPDFModal: boolean;
  createModalOpen: boolean;
  tableMenuFunctionLoading: boolean;
  tableDateEditing: boolean;
  analysChanged: boolean;
  sideMenuPinned: boolean;
  accessibilityMode: boolean;
  analysResultColumns: IAnalysResultColumn[];
  stopTableRendering: boolean;
  columnSelectOpen: boolean;
  selectedTab: number;
  useZeroSums: { org: boolean; updated: boolean };
  // moved summary/visual states
  expandedDetails: boolean;
  expandedTable: boolean;
  expandedAIInsights: boolean;
  expandedBarChart: boolean;
  expandedLineChart: boolean;
  expandedPieChart: boolean;
  expandedResultatDiagram: boolean;
  hideAllExpanded: boolean;
  printSections: IPrintSections;
}

const styles = (theme: Theme) => ({
  reportContainer: {
    display: 'flex',
    flex: '1 1 auto',
    margin: theme.spacing(2),
    maxWidth: `calc(100% - ${theme.spacing(12)})`,
    '@media print': {
      maxWidth: '100%',
      boxShadow: 'none',
    },
    position: 'relative',
  },
  sideMenuOpenMainWidth: {
    maxWidth: `calc(100% - ${theme.spacing(42)})`,
  },
  transparentReportContainer: {
    backgroundColor: 'transparent',
    boxShadow: 'none',
  },
});

const calculateIsChanges = (
  current: IFilledLocalAnalys,
  updated: ILocalAnalys,
  selectedAnalysChanged: boolean,
  impersonatedCodePrivilegeChanged: boolean,
  useZeroSums: { org: boolean; updated: boolean }
): boolean => {
  const {
    dates: currentDates,
    columns: currentColumns,
    dateInputType: currentDateInputType, // Vill ignorera
    ...restCurrent
  } = current;

  const {
    dates: updatedDates,
    columns: updatedColumns,
    dateInputType: updatedDateInputType, // Vill ignorera
    ...restUpdated
  } = updated;

  const datesDiffer = !equals(
    { ...currentDates },
    {
      from: {
        year: updatedDates.from.year,
        month: updatedDates.from.month,
        day: updatedDates.from.day,
      },
      to: {
        year: updatedDates.to.year,
        month: updatedDates.to.month,
        day: updatedDates.to.day,
      },
    }
  );

  const withoutDisplayLabel = (col: IColumn) => ({
    ...col,
    displayLabel: undefined,
  });

  const columnsDiffer = !equals(
    currentColumns.map(withoutDisplayLabel),
    updatedColumns.map(withoutDisplayLabel)
  );

  const errorsDiffer = !equals(
    { fromError: undefined, toError: undefined },
    { fromError: updatedDates.from.error, toError: updatedDates.to.error }
  );

  const otherDiffer = !equals(
    {
      ...restCurrent,
      other: { ...restCurrent.other, expandedSumIndex: 0 },
    },
    {
      ...restUpdated,
      other: { ...restCurrent.other, expandedSumIndex: 0 },
    }
  );

  const hasChanged = useZeroSums.org !== useZeroSums.updated;

  const isChanges =
    datesDiffer ||
    columnsDiffer ||
    errorsDiffer ||
    otherDiffer ||
    selectedAnalysChanged ||
    impersonatedCodePrivilegeChanged ||
    hasChanged;
  return isChanges;
};

/**
 * Hela vyn av EkonomiskAnalys
 */
export class EkonomiskAnalysMain extends React.Component<
  IExtendedProps,
  IState
> {
  focusSelect: boolean = false;
  timeout: any;
  scrollElement: any;
  sideMenuFromDateRef: React.RefObject<HTMLInputElement | null>;
  sideMenuToDateRef: React.RefObject<HTMLInputElement | null>;
  tableFromDateRef: React.RefObject<HTMLInputElement | null>;
  tableToDateRef: React.RefObject<HTMLInputElement | null>;

  constructor(props: IExtendedProps) {
    super(props);

    this.timeout = null;
    this.scrollElement = null;
    this.sideMenuFromDateRef = React.createRef<HTMLInputElement>();
    this.sideMenuToDateRef = React.createRef<HTMLInputElement>();
    this.tableFromDateRef = React.createRef<HTMLInputElement>();
    this.tableToDateRef = React.createRef<HTMLInputElement>();
    this.handleScroll = this.handleScroll.bind(this);
    this.handleTabSelection = this.handleTabSelection.bind(this);

    const isPinned =
      props.userInfo?.isSideMenuPinned ??
      (!props.selectedAnalys ||
        props.selectedAnalys.analys.id === NEW_ANALYS_RAPPORT_ID ||
        !props.userInfo?.updateStartanalysAtLogin);

    this.state = {
      urvalOchSummeringExpanded: Boolean(
        props.selectedAnalys?.analys.id === NEW_ANALYS_RAPPORT_ID
      ),
      columnSelectOpen: false,
      columnsExpanded: false,
      otherExpanded: false,
      showChartModal: false,
      showExcelModal: false,
      showPDFModal: false,
      showDetaljanalysPrintModal: false,
      createModalOpen: false,
      tableMenuFunctionLoading: false,
      tableDateEditing: false,
      analysChanged: false,
      sideMenuPinned: isPinned,
      accessibilityMode: false,
      analysResultColumns: [],
      selectedTab:
        (props.analysLevel !== AnalysLevelEnum.UNSET ||
          props.drilledFromDetaljanalys) &&
        props.selectedAnalys?.analys.categoryID ===
          ANALYSKATEGORI_ID_VISA_SAMMANFATTNING
          ? 1
          : 0,
      stopTableRendering:
        !this.props.userInfo?.updateStartanalysAtLogin ||
        props.selectedAnalys?.analys.id === NEW_ANALYS_RAPPORT_ID,
      useZeroSums: {
        org: this.props.userInfo?.useZeroSums ?? true,
        updated: this.props.userInfo?.useZeroSums ?? true,
      },
      expandedDetails: true,
      expandedTable: true,
      expandedAIInsights: true,
      expandedBarChart: true,
      expandedLineChart: true,
      expandedPieChart: true,
      expandedResultatDiagram: true,
      hideAllExpanded: false,
      printSections: {
        sammanfattning: true,
        resultatdiagram: true,
        stapeldiagram: true,
        linjediagram: true,
        cirkeldiagram: true,
        tabell: true,
        aiInsikter: true,
      },
    };
  }

  toggleColumnSelectOpen = () => {
    this.setState(({ columnSelectOpen }) => ({
      columnSelectOpen: !columnSelectOpen,
    }));
  };

  toggleSideMenuPinned =
    (saveDBSetting: EkonomiskAnalysSaveDBSettingType) => () => {
      this.setState(
        ({ sideMenuPinned }) => ({ sideMenuPinned: !sideMenuPinned }),
        () =>
          saveDBSetting('isSideMenuPinned', {
            id: '',
            value: this.state.sideMenuPinned ? 'Y' : 'N',
          })
      );
    };

  setAnalysResultColumns = (columns: IAnalysResultColumn[]) => {
    this.setState({
      analysResultColumns: columns,
    });
  };

  setTableDateEditing = (value: boolean) => {
    this.setState({
      tableDateEditing: value,
    });
  };

  setUseZeroSums = (updated: boolean) => {
    this.setState({
      useZeroSums: { org: this.state.useZeroSums.org, updated },
    });
  };

  setExpandedDetails = (value: boolean) => {
    this.setState({ expandedDetails: value });
  };

  setExpandedTable = (value: boolean) => {
    this.setState({ expandedTable: value });
  };

  setExpandedAIInsights = (value: boolean) => {
    this.setState({ expandedAIInsights: value });
  };

  setExpandedBarChart = (value: boolean) => {
    this.setState({ expandedBarChart: value });
  };

  setExpandedLineChart = (value: boolean) => {
    this.setState({ expandedLineChart: value });
  };

  setExpandedPieChart = (value: boolean) => {
    this.setState({ expandedPieChart: value });
  };

  setExpandedResultatDiagram = (value: boolean) => {
    this.setState({ expandedResultatDiagram: value });
  };

  openCreateModal = () => {
    this.setState({ createModalOpen: true });
  };

  closeCreateModal = () => {
    this.setState({ createModalOpen: false });
  };

  onAnalysSelected = (
    analys: ISelectedAnalysAnalys,
    codePrivilege: ISelectedAnalysCodePrivilege,
    sammanfattningsMode: boolean,
    newlyCreatedAnalys?: boolean
  ) => {
    this.setState(
      {
        analysChanged: true,
        stopTableRendering: false,
        urvalOchSummeringExpanded: !sammanfattningsMode,
        columnsExpanded: this.state.columnsExpanded && !sammanfattningsMode,
        otherExpanded: this.state.otherExpanded && !sammanfattningsMode,
        selectedTab: 0,
      },
      () => {
        const selectedAnalys = this.props.selectedAnalys;

        // Vill inte cleara urval om vi har valt samma analys igen
        if (
          !selectedAnalys ||
          !equals(analys, selectedAnalys.analys) ||
          !equals(codePrivilege, selectedAnalys.codePrivilege)
        ) {
          this.props.settingsContext.clearUrval();
        }

        const currentDateInputType =
          this.props.settingsContext.settingsForCurrentlyShown.dateInputType ??
          DateInputType.year;
        const updatedDateInputType =
          this.props.settingsContext.updatedSettings.dateInputType ??
          DateInputType.year;

        let dates = this.props.settingsContext.updatedSettings.dates;

        if (currentDateInputType < updatedDateInputType) {
          dates = this.props.originalStartDates;
        }

        if (
          !this.props.unsavedAnalys ||
          (this.props.unsavedAnalys &&
            (newlyCreatedAnalys ||
              !dateHasError(
                dates,
                this.props.settingsContext.updatedSettings.isInterval
              )))
        ) {
          this.props.setStartDates(dates);
        }
        this.props.onSelectedAnalysUpdated(analys, codePrivilege);
      }
    );
  };

  createNewAnalys = (analys: ILocalAnalys, selectedAnalys: ISelectedAnalys) => {
    this.setState(
      {
        analysChanged: true,
      },
      () => {
        this.props.settingsContext.createNewAnalys(analys);
        this.props.onSelectedAnalysUpdated(
          selectedAnalys.analys,
          selectedAnalys.codePrivilege,
          true
        );

        this.openSideMenu(SidePanels.urval)();
      }
    );
  };

  toggleChartModal = () => {
    this.setState(prevState => ({
      showChartModal: !prevState.showChartModal,
    }));
  };

  toggleExcelModal = () => {
    this.setState(prevState => ({
      showExcelModal: !prevState.showExcelModal,
    }));
  };

  togglePDFModal = () => {
    this.setState(prevState => ({
      showPDFModal: !prevState.showPDFModal,
    }));
  };

  toggleDetaljanalysPrintModal = () => {
    this.setState(prevState => ({
      showDetaljanalysPrintModal: !prevState.showDetaljanalysPrintModal,
    }));
  };

  handlePrint = (selectedSections: IPrintSections) => {
    // Store the selected sections in state
    this.setState({ printSections: selectedSections }, () => {
      // Close the modal
      this.toggleDetaljanalysPrintModal();

      // Small delay to ensure state updates are reflected in DOM
      setTimeout(() => {
        window.print();
      }, 100);
    });
  };

  /** Opens the sidemenu, will also open a panel if provided */
  openSideMenu = (panel?: SidePanels, callback?: () => void) => () => {
    if (panel != null) {
      if (panel === SidePanels.select) {
        this.focusSelect = true;
        this.setState({
          urvalOchSummeringExpanded: false,
          columnsExpanded: false,
          otherExpanded: false,
        });
      } else if (panel === SidePanels.urval) {
        this.setState({
          urvalOchSummeringExpanded: true,
          columnsExpanded: false,
          otherExpanded: false,
        });
      } else if (panel === SidePanels.columns) {
        // Kolumner och period
        this.setState({
          columnsExpanded: true,
          urvalOchSummeringExpanded: false,
          otherExpanded: false,
        });
      } else if (panel === SidePanels.other) {
        // Övriga val
        this.setState({
          otherExpanded: true,
          urvalOchSummeringExpanded: false,
          columnsExpanded: false,
        });
      }
      if (callback) {
        callback();
      }
    }
    this.props.toggleSideMenuOpen(true, callback);
  };

  closeSideMenu = () => {
    this.props.toggleSideMenuOpen(false);
  };

  setTwoDigit = (value: string | number) => {
    if (value.toString().length === 1) {
      return `0${value}`;
    }

    return value.toString();
  };

  getPeriodsComponent = (
    dense?: boolean,
    shouldUpdateDirect: boolean = false,
    simple?: boolean,
    fromDateRef?: React.MutableRefObject<HTMLInputElement | null>,
    toDateRef?: React.MutableRefObject<HTMLInputElement | null>,
    isChanges?: boolean
  ): React.ReactNode => {
    if (!this.props.settingsContext.settingsForCurrentlyShown) {
      return null;
    }

    const { settingsContext } = this.props;
    const { updatedSettings } = settingsContext;
    const isPeriodLocked = this.props.isPeriodLocked(
      updatedSettings.dates.from.year,
      updatedSettings.dates.from.month,
      updatedSettings.dates.to.month,
      updatedSettings.dateInputType
    );

    return (
      <EkonomiskAnalysYearValidator>
        {({ validateYear }) => (
          <EkonomiskAnalysPeriodComponent
            dateInputType={updatedSettings.dateInputType}
            isInterval={updatedSettings.isInterval}
            dates={updatedSettings.dates}
            periodLocked={!simple ? isPeriodLocked : undefined}
            isEditing={simple ? true : this.state.tableDateEditing}
            setIsEditing={simple ? () => {} : this.setTableDateEditing}
            simple={simple}
            onFromDateChange={(date, changeBoth) =>
              settingsContext.onFromDateChange(date, validateYear, changeBoth)
            }
            onToDateChange={date =>
              settingsContext.onToDateChange(date, validateYear, undefined)
            }
            fromDateRef={fromDateRef}
            toDateRef={toDateRef}
            dense={dense}
            isChanges={isChanges}
            updateReport={this.updateReport}
            accessibilityMode={this.state.accessibilityMode}
            marginRight
          />
        )}
      </EkonomiskAnalysYearValidator>
    );
  };

  handleSideMenuExpansion =
    (panel: SidePanels, callback?: () => void) => () => {
      if (panel === SidePanels.urval) {
        this.setState(state => ({
          urvalOchSummeringExpanded: !state.urvalOchSummeringExpanded,
          columnsExpanded: false,
          otherExpanded: false,
        }));
      }
      if (panel === SidePanels.columns) {
        this.setState(state => ({
          urvalOchSummeringExpanded: false,
          columnsExpanded: !state.columnsExpanded,
          otherExpanded: false,
        }));
      }
      if (panel === SidePanels.other) {
        this.setState(state => ({
          urvalOchSummeringExpanded: false,
          columnsExpanded: false,
          otherExpanded: !state.otherExpanded,
        }));
      }
      if (callback) {
        callback();
      }
    };

  handleScroll = (event: React.UIEvent) => {
    let scrollPos: number | null = null;
    let tableTypeAddSpace: number =
      this.props.settingsContext.settingsForCurrentlyShown.type ===
      'RAPPORTDIMENSIONRADDRIVEN'
        ? 0
        : 24;

    if (this.scrollElement === null) {
      this.scrollElement = event.currentTarget;
    }
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
    this.timeout = setTimeout(() => {
      if (this.scrollElement) {
        scrollPos =
          this.scrollElement.lastChild.scrollLeft > 0
            ? this.scrollElement.lastChild.scrollLeft
            : this.scrollElement.scrollLeft;
      }
      let detailTables: NodeListOf<HTMLElement> =
        document.querySelectorAll('[data-details]');
      Array.from(detailTables).map((el, i) => {
        let curElement = detailTables[i];
        if (scrollPos !== null) {
          curElement.style.left = scrollPos + tableTypeAddSpace + 'px';
        }
      });
      this.timeout = null;
      this.scrollElement = null;
    }, 30);
  };

  handleTabSelection = (newValue: number) => {
    this.setState({ selectedTab: newValue });
  };

  reset = () => {
    this.props.updateImpersonatedCodePrivilege();
    this.props.setStartDates(this.props.originalStartDates);
    this.props.settingsContext.reset();
  };

  updateReport = (updateOrig?: boolean) => {
    this.setState(
      {
        analysChanged: false,
        stopTableRendering: false,
        useZeroSums: {
          org: this.props.userInfo?.useZeroSums ?? true,
          updated: this.props.userInfo?.useZeroSums ?? true,
        },
      },
      () => {
        if (!this.state.sideMenuPinned) {
          this.closeSideMenu();
        }
        this.props.settingsContext.update(updateOrig);
      }
    );
  };

  onToggleExpandedSections = () => {
    this.setState(prevState => ({
      expandedDetails: prevState.hideAllExpanded,
      expandedTable: prevState.hideAllExpanded,
      expandedAIInsights: prevState.hideAllExpanded,
      expandedBarChart: prevState.hideAllExpanded,
      expandedLineChart: prevState.hideAllExpanded,
      expandedPieChart: prevState.hideAllExpanded,
      expandedResultatDiagram: prevState.hideAllExpanded,
      hideAllExpanded: !prevState.hideAllExpanded,
    }));
  };

  render() {
    const { selectedAnalys, unsavedAnalys, setStartDates, analysType } =
      this.props;

    const {
      settingsForCurrentlyShown,
      updatedSettings,
      originalSettings,
      changedSections,
      onColumnsChanged,
      onSumsChanged,
      onUrvalChanged,
      onDecimalsChanged,
      onDenominationChanged,
      toggleEndastBokfört,
      toggleShowComparison,
      changedFromOriginal,
      kodbehorighetsUrval,
      grundurvalUrval,
      availableReports,
      onSummationModeChanged,
      setRefetchAnalys,
      onExkluderaPersonalkostnaderChanged,
    } = this.props.settingsContext;

    const isChanges = calculateIsChanges(
      settingsForCurrentlyShown,
      updatedSettings,
      this.state.analysChanged,
      this.props.impersonatedCodePrivilegeChanged,
      this.state.useZeroSums
    );

    // Används för att bestämma om perioden är låst eller inte.
    const selectedYear = updatedSettings.dates.from.year;
    const selectedMonth = updatedSettings.dates.from.month;
    const selectedToMonth = updatedSettings.dates.to.month;

    // selectedAnalys blir icke-null utan att ha data av någon anledning
    const lopandeAnalys =
      settingsForCurrentlyShown.type === 'RAPPORTDIMENSIONLOPANDE';

    const sammanfattningsMode = Boolean(
      updatedSettings.categoryID &&
        updatedSettings.categoryID === ANALYSKATEGORI_ID_VISA_SAMMANFATTNING
    );

    const userInfoLoading = Boolean(
      this.props.userInfo &&
        this.props.userInfo.startAnalys &&
        this.props.userInfo.startAnalys.rapportId !==
          USE_NEW_AS_START_ANALYS_ID &&
        settingsForCurrentlyShown.name === ''
    );

    const dateError = dateHasError(
      updatedSettings.dates,
      updatedSettings.isInterval
    );

    const collection = findCollectionStatic('EKAnalys');

    const showUpdateOverlay =
      Boolean(selectedAnalys && isChanges) ||
      (this.state.stopTableRendering && Boolean(selectedAnalys));

    const expandSectionBtnLabel = () => {
      const allExpanded =
        this.state.expandedDetails &&
        this.state.expandedTable &&
        this.state.expandedAIInsights &&
        this.state.expandedBarChart &&
        this.state.expandedLineChart &&
        this.state.expandedPieChart;

      return allExpanded || !(allExpanded || this.state.hideAllExpanded)
        ? 'Dölj alla'
        : 'Visa alla';
    };

    //Kolla om vi har en eller flera kolumner av typen intäkt/kostnad för att avgöra om resultatdiagrammet ska visas
    const showResultatDiagram = Boolean(
      updatedSettings.columns.some(
        col =>
          col.data.SPECIFIKATION === SpecifikationTypeEnum.INTAKT ||
          col.data.SPECIFIKATION === SpecifikationTypeEnum.KOSTNAD
      )
    );

    const showFordelningDiagram = Boolean(
      analysType === AnalysTypeEnum.INTAKTSUPPFOLJNING ||
        analysType === AnalysTypeEnum.KOSTNADSUPPFOLJNING
    );

    return (
      <EkonomiskAnalysHasPeriodiseradBudgetQuery
        year={updatedSettings.dates.from.year}
        databaseName={this.props.activeDatabaseName}
      >
        {hasPeriodiseradBudget => (
          <EkonomiskAnalysContextProvider
            value={{
              accessibilityMode: this.state.accessibilityMode,
              analysResultColumns: this.state.analysResultColumns,
              codeSystemId: this.props.codeSystemId,
              columnSelectOpen: this.state.columnSelectOpen,
              dbName: this.props.activeDatabaseName,
              grundurvalUrval,
              harPeriodiseradBudget: hasPeriodiseradBudget,
              hasWholeCodeBook: this.props.hasWholeCodeBook,
              kodbehorighetsUrval,
              leftSidebarOpen: this.props.sideMenuOpen,
              isChanges,
              urval: updatedSettings.urval || null,
              radBaserad:
                settingsForCurrentlyShown.type === 'RAPPORTDIMENSIONRADDRIVEN',
              setAnalysResultColumns: this.setAnalysResultColumns,
              setStartDates,
              setUrval: onUrvalChanged,
              setUseZeroSums: this.setUseZeroSums,
              standardColumns: this.props.standardColumns,
              userInfo: this.props.userInfo,
              updateReport: this.updateReport,
              useZeroSums: this.state.useZeroSums,
            }}
          >
            <EkonomiskAnalysSideMenu
              sideMenuIsOpen={this.props.sideMenuOpen}
              openSideMenu={this.openSideMenu}
              closeSideMenu={this.closeSideMenu}
              onAnalysSelected={this.onAnalysSelected}
              handleExpansion={this.handleSideMenuExpansion}
              onUrvalChanged={onUrvalChanged}
              onSumsChanged={onSumsChanged}
              selectedAnalys={selectedAnalys ? selectedAnalys : null}
              activeDatabaseName={this.props.activeDatabaseName}
              startAnalysId={
                selectedAnalys ? selectedAnalys.analys.id : undefined
              }
              newAnalysSettings={updatedSettings}
              onExpandedSumIndexChange={
                this.props.settingsContext.onExpandedSumIndexChange
              }
              expandedSumIndex={
                this.props.settingsContext.updatedSettings.other
                  .expandedSumIndex
              }
              currentAnalysSettings={settingsForCurrentlyShown}
              urvalOchSummeringExpanded={this.state.urvalOchSummeringExpanded}
              codeSystemId={this.props.codeSystemId}
              getPeriodsComponent={(dense, shouldUpdateDirect, simple) =>
                this.getPeriodsComponent(
                  dense,
                  shouldUpdateDirect,
                  simple,
                  this.sideMenuFromDateRef,
                  this.sideMenuToDateRef
                )
              }
              onDenominationChange={onDenominationChanged}
              columnsExpanded={this.state.columnsExpanded}
              onColumnsChanged={onColumnsChanged}
              otherExpanded={this.state.otherExpanded}
              denomination={updatedSettings.other.denomination}
              nrDecimals={updatedSettings.other.decimals}
              decimalsChanged={onDecimalsChanged}
              settingsChangedFromOriginal={changedFromOriginal}
              showComparison={updatedSettings.other.showComparison}
              toggleShowComparison={toggleShowComparison}
              toggleEndastBokfört={toggleEndastBokfört}
              summeringChanged={changedSections.includes('summering')}
              urvalChanged={changedSections.includes('urval')}
              ovrigaValChanged={changedSections.includes('other')}
              periodKolumnerChanged={changedSections.includes('periodKolumner')}
              resetAnalysSettings={this.reset}
              periodLocked={this.props.isPeriodLocked(
                selectedYear,
                selectedMonth,
                selectedToMonth,
                updatedSettings.dateInputType
              )}
              collectionMap={this.props.collectionMap}
              availableReports={availableReports}
              createNewAnalys={this.createNewAnalys}
              unsavedAnalys={unsavedAnalys}
              impersonatedCodePrivilege={this.props.impersonatedCodePrivilege}
              createNewModalOpen={this.state.createModalOpen}
              openCreateNewModal={this.openCreateModal}
              closeCreateNewModal={this.closeCreateModal}
              isLopandeAnalys={lopandeAnalys}
              userInfo={this.props.userInfo}
              fromDateRef={this.sideMenuFromDateRef}
              toDateRef={this.sideMenuToDateRef}
              onBudgetSummationModeChanged={
                this.props.settingsContext.onBudgetSummationModeChanged
              }
              onSummationModeChanged={onSummationModeChanged}
              sideMenuPinned={this.state.sideMenuPinned}
              toggleSideMenuPinned={this.toggleSideMenuPinned(
                this.props.saveDBSetting
              )}
              onExkluderaPersonalkostnaderChanged={
                onExkluderaPersonalkostnaderChanged
              }
              updateReport={this.updateReport}
              onVisaSummeringarChanged={
                this.props.settingsContext.onVisaSummeringarChanged
              }
              disableAll={this.props.hasRutinStartError}
              sammanfattningsMode={sammanfattningsMode}
              isInDetaljanalysTab={this.state.selectedTab === 1}
            />

            {this.props.hasRutinStartError && collection ? (
              <EDPaper
                elevation={2}
                className={classNames(this.props.classes.reportContainer, {
                  [this.props.classes.sideMenuOpenMainWidth]:
                    this.props.sideMenuOpen,
                })}
                square={false}
              >
                <EmptyReason
                  urval={null}
                  hasData={false}
                  loading={false}
                  refetch={() => {}}
                  collection={collection}
                />
              </EDPaper>
            ) : (
              <>
                <Portals />
                <EDPaper
                  elevation={2}
                  className={classNames(this.props.classes.reportContainer, {
                    [this.props.classes.sideMenuOpenMainWidth]:
                      this.props.sideMenuOpen,
                    [this.props.classes.transparentReportContainer]:
                      this.state.selectedTab === 1 && sammanfattningsMode,
                  })}
                  square={false}
                >
                  <UpdateOverlay
                    display={showUpdateOverlay && this.state.selectedTab !== 1}
                    updateActive={
                      (isChanges || this.state.stopTableRendering) && !dateError
                    }
                    updateTooltip={
                      dateError
                        ? 'Period måste anges'
                        : 'Uppdatera analysen enl. gjorda inställningar (Alt+A)'
                    }
                    update={this.updateReport}
                    handleTabSelection={this.handleTabSelection}
                  />
                  {selectedAnalys == null && (
                    <EkonomiskAnalysNoAnalysSelectedPage
                      openSideMenu={this.openSideMenu}
                      openCreateNew={this.openCreateModal}
                    />
                  )}
                  {selectedAnalys &&
                    updatedSettings.categoryID &&
                    updatedSettings.categoryID ===
                      ANALYSKATEGORI_ID_VISA_SAMMANFATTNING && (
                      <EDBox
                        sx={{
                          position: 'absolute',
                          zIndex: 120,
                          top: theme => `-${theme.spacing(2)}`,
                          left: theme => `${theme.spacing(0)}`,
                          right: theme => `${theme.spacing(0)}`,
                          '@media print': {
                            display: 'none',
                          },
                        }}
                      >
                        <EDTabs
                          value={this.state.selectedTab}
                          onChange={(value: number) => {
                            this.handleTabSelection(value);
                            if (value === 1) {
                              this.closeSideMenu();
                            }
                          }}
                          subtleTabs
                          noBoxShadow
                          style={{
                            width: '100%',
                          }}
                        >
                          <EDTab
                            style={{ color: 'black', marginLeft: '16px' }}
                            label="Rapport"
                          />
                          <EDTab
                            style={{ color: 'black' }}
                            label="Detaljanalys"
                            disabled={showUpdateOverlay}
                          />
                        </EDTabs>
                        <EDBox
                          sx={{
                            position: 'absolute',
                            top: 0,
                            right: 2,
                            zIndex: 1,
                            display:
                              this.state.selectedTab === 0 ? 'none' : 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                          }}
                        >
                          <EDButton
                            outlined
                            padding="3px"
                            onClick={this.onToggleExpandedSections}
                          >
                            {expandSectionBtnLabel()}
                          </EDButton>
                          <FunctionMenu id="ekonomiskAnalys">
                            <FunctionItem
                              onClick={this.toggleDetaljanalysPrintModal}
                              iconType="print"
                            >
                              Skriv ut detaljanalys
                            </FunctionItem>
                          </FunctionMenu>
                        </EDBox>
                      </EDBox>
                    )}
                  {selectedAnalys && (
                    <EDBox
                      className="print-container"
                      sx={{
                        width: '100%',
                        pr: this.state.selectedTab === 1 ? 0 : 3,
                        pl: this.state.selectedTab === 1 ? 0 : 3,
                        pb: this.state.selectedTab === 1 ? 0 : 3,
                        pt: theme =>
                          this.state.selectedTab === 1
                            ? theme.spacing(3)
                            : updatedSettings.categoryID ===
                              ANALYSKATEGORI_ID_VISA_SAMMANFATTNING
                            ? theme.spacing(3)
                            : 0,
                        overflowX: 'hidden',
                        ...(this.state.selectedTab === 1 && {
                          '& .MuiAccordion-root': {
                            borderRadius: '5px',
                            overflow: 'hidden',
                          },
                          '& .MuiAccordionSummary-root': {
                            padding: theme => theme.spacing(2, 0, 2, 2),
                            '&:hover': {
                              background: theme =>
                                theme.customProperties.Fade.BlueGray.hover,
                            },
                          },
                        }),
                        '@media print': {
                          overflow: 'visible',
                          height: 'auto',
                          maxHeight: 'none',
                          p: 0,
                        },
                      }}
                    >
                      {this.state.selectedTab === 0 && (
                        <EDBox
                          onScroll={this.handleScroll}
                          className="print-scroll-container"
                          sx={{
                            boxSizing: 'border-box',
                            overflow: 'auto',
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%',
                            '@media print': {
                              overflow: 'visible',
                              height: 'auto',
                              maxHeight: 'none',
                            },
                          }}
                        >
                          <EkonomiskAnalysHeader
                            logo={this.props.logo}
                            selectedAnalys={selectedAnalys}
                            database={this.props.activeDatabaseName}
                            anvandarUrval={settingsForCurrentlyShown.urval}
                            currentDates={settingsForCurrentlyShown.dates}
                            updatedSettings={updatedSettings}
                            showChartModal={this.toggleChartModal}
                            updateReport={this.updateReport}
                            showExcelModal={this.toggleExcelModal}
                            togglePDFModal={this.togglePDFModal}
                            isChanges={isChanges}
                            periodsComponent={this.getPeriodsComponent}
                            endastBokfort={
                              !updatedSettings.other.includeEjBokfordaJournaler
                            }
                            forceHelarsjamforelse={getForceHelarsJamforelse(
                              hasPeriodiseradBudget,
                              this.props.userInfo?.preferHelarsjamforelse
                            )}
                            columns={settingsForCurrentlyShown.columns.map(
                              x => x.data
                            )}
                            sums={settingsForCurrentlyShown.sums}
                            isLopandeAnalys={lopandeAnalys}
                            impersonatedCodePrivilege={
                              this.props.impersonatedCodePrivilege
                            }
                            baseSelectionId={
                              selectedAnalys.analys.grundurval
                                ? selectedAnalys.analys.grundurval.id
                                : undefined
                            }
                            detaljUrval={settingsForCurrentlyShown.detaljUrval}
                            fromDateRef={this.tableFromDateRef}
                            toDateRef={this.tableToDateRef}
                            toggleColumnSelectOpen={this.toggleColumnSelectOpen}
                            sammanfattningsMode={sammanfattningsMode}
                          />
                          <EkonomiskAnalysTableBase
                            lopandeAnalys={lopandeAnalys}
                            settingsForCurrentlyShown={
                              settingsForCurrentlyShown
                            }
                            impersonatedCodePrivilege={
                              this.props.impersonatedCodePrivilege
                            }
                            selectedAnalys={selectedAnalys}
                            settingsChanged={isChanges}
                            updateImpersonatedCodePrivilege={
                              this.props.updateImpersonatedCodePrivilege
                            }
                            onDenominationChanged={onDenominationChanged}
                            expandedSumIndex={
                              this.props.settingsContext.updatedSettings.other
                                .expandedSumIndex
                            }
                            handleScroll={this.handleScroll}
                            updatedSettings={updatedSettings}
                            userInfoLoading={userInfoLoading}
                            setRefetchAnalys={setRefetchAnalys}
                            stopTableRendering={this.state.stopTableRendering}
                            sammanfattningsMode={sammanfattningsMode}
                          />
                        </EDBox>
                      )}

                      {this.state.selectedTab === 1 && (
                        <EkonomiskAnalysSammanfattning
                          availableReports={availableReports}
                          analysType={analysType}
                          settingsForCurrentlyShown={settingsForCurrentlyShown}
                          impersonatedCodePrivilege={
                            this.props.impersonatedCodePrivilege
                          }
                          selectedAnalys={selectedAnalys}
                          settingsChanged={isChanges}
                          updatedSettings={updatedSettings}
                          originalSettings={originalSettings}
                          setRefetchAnalys={setRefetchAnalys}
                          stopTableRendering={this.state.stopTableRendering}
                          logo={this.props.logo}
                          analysLevel={this.props.analysLevel}
                          loading={userInfoLoading}
                          expandedDetails={this.state.expandedDetails}
                          setExpandedDetails={this.setExpandedDetails}
                          expandedTable={this.state.expandedTable}
                          setExpandedTable={this.setExpandedTable}
                          expandedAIInsights={this.state.expandedAIInsights}
                          setExpandedAIInsights={this.setExpandedAIInsights}
                          expandedBarChart={this.state.expandedBarChart}
                          setExpandedBarChart={this.setExpandedBarChart}
                          expandedLineChart={this.state.expandedLineChart}
                          setExpandedLineChart={this.setExpandedLineChart}
                          expandedPieChart={this.state.expandedPieChart}
                          setExpandedPieChart={this.setExpandedPieChart}
                          expandedResultatDiagram={
                            this.state.expandedResultatDiagram
                          }
                          setExpandedResultatDiagram={
                            this.setExpandedResultatDiagram
                          }
                          printSections={this.state.printSections}
                          showFordelningDiagram={showFordelningDiagram}
                          showResultatDiagram={showResultatDiagram}
                        />
                      )}
                    </EDBox>
                  )}
                </EDPaper>
                {selectedAnalys && settingsForCurrentlyShown && (
                  <EkonomiskAnalysChartModal
                    open={this.state.showChartModal}
                    onClose={this.toggleChartModal}
                    availableColumns={settingsForCurrentlyShown.columns.filter(
                      col => !col.data.HIDE_COLUMN
                    )}
                    chartSettings={{
                      id: selectedAnalys.analys.id,
                      kodbehorighetId: this.props.impersonatedCodePrivilege
                        ? this.props.impersonatedCodePrivilege.id
                        : selectedAnalys.codePrivilege.id,
                      grundurvalId: selectedAnalys.analys.grundurval
                        ? selectedAnalys.analys.grundurval.id
                        : undefined,
                      analys: {
                        ...settingsForCurrentlyShown,

                        columns: settingsForCurrentlyShown.columns.filter(
                          col => !col.data.HIDE_COLUMN
                        ),
                      },
                    }}
                    denomination={settingsForCurrentlyShown.other.denomination}
                    nrDecimals={Number(
                      settingsForCurrentlyShown.other.decimals
                    )}
                    isPeriodLocked={this.props.isPeriodLocked}
                    forceHelarsjamforelse={getForceHelarsJamforelse(
                      hasPeriodiseradBudget,
                      this.props.userInfo?.preferHelarsjamforelse
                    )}
                  />
                )}
                {selectedAnalys && (
                  <EkonomiskAnalysExcelModal
                    open={this.state.showExcelModal}
                    onClose={this.toggleExcelModal}
                    selectedAnalys={selectedAnalys}
                    radbaserad={
                      settingsForCurrentlyShown.type ===
                      'RAPPORTDIMENSIONRADDRIVEN'
                    }
                    analysSettings={settingsForCurrentlyShown}
                    impersonatedCodePrivilege={
                      this.props.impersonatedCodePrivilege
                    }
                    isPeriodLocked={this.props.isPeriodLocked}
                    forceHelarsjamforelse={getForceHelarsJamforelse(
                      hasPeriodiseradBudget,
                      this.props.userInfo?.preferHelarsjamforelse
                    )}
                  />
                )}
                {selectedAnalys && (
                  <ExportToPDF<IEkonomiskAnalysPDF>>
                    {exportToPDF => (
                      <EkonomiskAnalysPrintPDFModal
                        exportToPDF={exportToPDF}
                        open={this.state.showPDFModal}
                        onClose={this.togglePDFModal}
                        analysSettings={settingsForCurrentlyShown}
                        selectedAnalys={selectedAnalys}
                        impersonatedCodePrivilege={
                          this.props.impersonatedCodePrivilege
                        }
                        radbaserad={
                          settingsForCurrentlyShown.type ===
                          'RAPPORTDIMENSIONRADDRIVEN'
                        }
                        isPeriodLocked={this.props.isPeriodLocked}
                        forceHelarsjamforelse={getForceHelarsJamforelse(
                          hasPeriodiseradBudget,
                          this.props.userInfo?.preferHelarsjamforelse
                        )}
                      />
                    )}
                  </ExportToPDF>
                )}

                {selectedAnalys && (
                  <DetaljanalysPrintModal
                    open={this.state.showDetaljanalysPrintModal}
                    onClose={this.toggleDetaljanalysPrintModal}
                    onPrint={this.handlePrint}
                    analysType={analysType}
                    showFordelningDiagram={showFordelningDiagram}
                    showResultatDiagramSection={showResultatDiagram}
                  />
                )}
              </>
            )}
            <AvailableColumnTemplatesHandler>
              {availableColumnTemplates => (
                <EkonomiskAnalysColumnSelectDialog
                  selectedColumns={updatedSettings.columns}
                  dates={updatedSettings.dates}
                  dialogOpen={this.state.columnSelectOpen}
                  dialogOnClose={this.toggleColumnSelectOpen}
                  onUpdate={onColumnsChanged}
                  isInterval={updatedSettings.isInterval}
                  availableColumnTemplates={availableColumnTemplates}
                  selectedAnalys={selectedAnalys}
                />
              )}
            </AvailableColumnTemplatesHandler>
          </EkonomiskAnalysContextProvider>
        )}
      </EkonomiskAnalysHasPeriodiseradBudgetQuery>
    );
  }
}

const EkonomiskAnalysExtended = (props: IProps) => {
  return (
    <EnhancedBokforingsarActions>
      {({ isPeriodLocked }) => {
        return (
          <EkonomiskAnalysSaveDBSettingMutation>
            {saveDBSetting => (
              <EkonomiskAnalysMain
                {...props}
                isPeriodLocked={isPeriodLocked || (() => true)}
                saveDBSetting={saveDBSetting}
              />
            )}
          </EkonomiskAnalysSaveDBSettingMutation>
        );
      }}
    </EnhancedBokforingsarActions>
  );
};

// En komponent som renderar BackButton och Header i portaler så att de kan positioneras i Breadcrumbs-komponentens layout.
const Portals = () => {
  const {
    headerReferences,
    backButtonReferences,
    currentNavLevel,
    registerActiveNavLevel,
    unregisterActiveNavLevel,
  } = useContext(BreadcrumbsContext);

  useEffect(() => {
    registerActiveNavLevel(0);
    return () => unregisterActiveNavLevel(0);
  }, [registerActiveNavLevel, unregisterActiveNavLevel]);

  const listHeaderRef = headerReferences?.[currentNavLevel ?? 0];
  const detailBackRef = backButtonReferences?.[1];
  const baseRouteValues = useBaseRouteValue();

  return (
    <>
      {detailBackRef &&
        createPortal(
          <StyledEngineProvider injectFirst>
            <ThemeProvider theme={AvenyThemeDark}>
              <RouterLink to={buildBasePath(baseRouteValues)}>
                <EDIconButton
                  icon="arrow_back"
                  iconColor={'inherit'}
                  tooltip={'Startsidan'}
                  padding="dense"
                  marginTop={0.5}
                  marginBottom={0.5}
                />
              </RouterLink>
            </ThemeProvider>
          </StyledEngineProvider>,
          detailBackRef
        )}
      {listHeaderRef &&
        listHeaderRef[HEADER] &&
        listHeaderRef[HEADER].current &&
        createPortal('Ekonomisk Analys', listHeaderRef[HEADER].current)}
    </>
  );
};

export default withStyles(EkonomiskAnalysExtended, styles);
